import { View } from "react-native";
import { IOrder, OrderMode, OrderStatus, OrderChannel, ItemType, convertToEuros, getOrderDisplayAddress, isPlatformOrder } from "@app/common";
import { memo, useState } from "react";
import styled from "@2pulse/responsive";
import { normalize } from "@app/helpers";
import { theme } from "@app/theme";
import { DateTime } from "luxon";
import { SvgDelivery } from "@app/components/icons/SvgDelivery";
import { SvgTakeaway } from "@app/components/icons/SvgTakeaway";
import { SvgEat } from "@app/components/icons/SvgEat";
import { FilterButton } from "@app/components";
import { CheckPermissions } from "@app/components/CheckPermissions";
import { useToast } from "@app/hooks";
import { useModal } from "@app/hooks/useModal";
import { fiscalApi } from "@app/store";
import { extractApiGenericError } from "@app/helpers";
import { ReleaseEcoleModal } from "./ReleaseEcoleModal";

interface OrderCardProps {
  order: IOrder;
}

const renderOrderModeIcon = (mode: OrderMode, dark?: boolean) => {
  const iconProps = {
    strokePath: dark ? theme.colors.oxfordblue : theme.colors.babypowder,
    width: normalize(20),
    height: normalize(20),
  };

  switch (mode) {
    case OrderMode.DELIVERY:
      return <SvgDelivery {...iconProps} />;
    case OrderMode.TAKEAWAY:
      return <SvgTakeaway {...iconProps} />;
    case OrderMode.ONSPOT:
      return <SvgEat {...iconProps} />;
    default:
      return null;
  }
};

export const OrderCard = memo(({ order }: OrderCardProps) => {
  const [showContent, setShowContent] = useState(false);

  return (
    <OrderCardContainer isOpen={showContent}>
      <OrderCardTrigger testID="ouvrir-commande" onPress={() => setShowContent(!showContent)}>
        <OrderCardData first>#{order.serviceId}</OrderCardData>
        <OrderCardData dark={showContent}>
          {renderOrderModeIcon(order.mode, showContent)}
        </OrderCardData>

        <OrderCardData
          pill
          pillColor={getChannelPillColors(order.channel).bg}
          pillBorderColor={getChannelPillColors(order.channel).border}
        >
          {getChannelLabel(order.channel)}
        </OrderCardData>

        {order.status === OrderStatus.CANCELLED && (
          <>
            &nbsp;&nbsp;<OrderCardData pill status={order.status}>Annulée</OrderCardData>
          </>
        )}

        {order.fiscalEcole === true && (
          <>
            &nbsp;&nbsp;<OrderCardData testID="pastille-ecole" pill pillColor="#FFC727" pillBorderColor="#E8BA21">École</OrderCardData>
          </>
        )}

        <OrderCardData first dark={showContent}>{DateTime.fromISO(order.createdAt).toFormat('dd/MM HH:mm')}</OrderCardData>
        <OrderCardData fixed dark={showContent}>{convertToEuros(order.total)}€</OrderCardData>

        {order.customer?.firstname && (
          <OrderCardData dark={showContent}>
            {(order.customer?._id && !isPlatformOrder(order)) ? (
              <OrderCardLink href={`/clients?_id=${order.customer?._id}`}>
                {order.customer?.firstname} {order.customer?.lastname}
              </OrderCardLink>
            ) : (
              `${order.customer?.firstname} ${order.customer?.lastname}`
            )}
          </OrderCardData>
        )}

        {order.customer?.phone && (
          <OrderCardData dark={showContent}>{order.customer?.phone}</OrderCardData>
        )}

        {!!getOrderDisplayAddress(order) && (
          <OrderCardData dark={showContent}>{getOrderDisplayAddress(order)}</OrderCardData>
        )}

        {order.platformCode && (
          <OrderCardData dark={showContent}>#{order.platformCode}</OrderCardData>
        )}

      </OrderCardTrigger>
      {showContent && (
        <OrderCardContent>
          {order.cancelReason?.reason && (
            <OrderCardContentDisclaimer>Motif de refus :{order.cancelReason?.reason}</OrderCardContentDisclaimer>
          )}

          {order.payments?.length > 0 && (
            <OrderCardContentDisclaimer>Méthodes de paiement : {order.payments.map((payment) => payment.method.name).join(', ')}</OrderCardContentDisclaimer>
          )}

          {order.platformCode && (
            <OrderCardContentDisclaimer>Numéro de commande plateforme : {order.platformCode}</OrderCardContentDisclaimer>
          )}

          {order.mode === OrderMode.DELIVERY && !!getOrderDisplayAddress(order) && (
            <OrderCardContentDisclaimer>Adresse de livraison : {getOrderDisplayAddress(order)}</OrderCardContentDisclaimer>
          )}

          {order.mode === OrderMode.DELIVERY && !!order.address?.informations?.trim() && (
            <OrderCardContentDisclaimer>Consignes de livraison : {order.address.informations}</OrderCardContentDisclaimer>
          )}

          {order.fiscalEcole === true && (
            <CheckPermissions permissions={['dashboard::fiscal.manage']}>
              <OrderEcoleActions order={order} />
            </CheckPermissions>
          )}

          <OrderCardContentRow noBorder>
            <OrderCardSubData>Produits :</OrderCardSubData>
          </OrderCardContentRow>

          {order?.items?.map((item, idx) => {
            const isProduct = item.type === ItemType.PRODUCT;
            const itemName = item.item.name;
            const quantity = (item.item as any).quantity ?? item.quantity ?? 1;
            const variationName = isProduct ? (item.item as any).productVariation?.name : undefined;
            const packVariations = !isProduct ? (item.item as any).packToProductVariations || [] : [];
            const optionNames = isProduct
              ? ((item.item as any).options || []).flatMap((opt: any) => (opt.products || []).map((p: any) => p.name))
              : [];
            const title = isProduct ? (variationName ?? itemName) : itemName;

            return (
              <View key={`item-${idx}`}>
                <OrderCardContentRow noBorder>
                  <OrderCardSubData>
                    - {title} x{quantity}
                  </OrderCardSubData>
                </OrderCardContentRow>
                {!isProduct && packVariations.length > 0 && (
                  <OrderCardContentRow noBorder>
                    <OrderCardSubData>
                      &nbsp;&nbsp; <small>- {packVariations.map((pv: any) => pv?.productVariation?.name).filter(Boolean).join(', ')}</small>
                    </OrderCardSubData>
                  </OrderCardContentRow>
                )}
                {optionNames.length > 0 && (
                  <OrderCardContentRow noBorder>
                    <OrderCardSubData>
                      &nbsp;&nbsp; <small>Options: {optionNames.join(', ')}</small>
                    </OrderCardSubData>
                  </OrderCardContentRow>
                )}
              </View>
            );
          })}

          {(order.deliveryFees || 0) > 0 &&
            <OrderCardContentRow>
              <OrderCardSubData>Frais de livraison : </OrderCardSubData><OrderCardSubData>{convertToEuros(order.deliveryFees || 0)}€</OrderCardSubData>
            </OrderCardContentRow>
          }
          {(order.deliveryMan &&
            <OrderCardContentRow>
              <OrderCardSubData>Livreur : </OrderCardSubData> <OrderCardSubData>{order.deliveryMan.firstname} {order.deliveryMan.lastname}</OrderCardSubData>
            </OrderCardContentRow>
          )}
          {order.promotions && order.promotions.length > 0 && (
            <>
              <OrderCardContentRow noBorder>
                <OrderCardSubData>Promotions :</OrderCardSubData>
              </OrderCardContentRow>
              {order.promotions.map((promotion, idx) => {
                const label = (promotion as any).name || (promotion as any).code || 'Promotion';
                const discount = (promotion as any).computedDiscount ?? (promotion as any).totalDiscount ?? (promotion as any).orderDiscount ?? 0;
                return (
                  <OrderCardContentRow key={`promo-${idx}`} noBorder>
                    <OrderCardSubData>{label}</OrderCardSubData>
                    <OrderCardSubData>-{convertToEuros(discount)}€</OrderCardSubData>
                  </OrderCardContentRow>
                )
              })}
            </>
          )}
          <OrderCardContentRow>
            <OrderCardSubData>Total : </OrderCardSubData><OrderCardSubData>{convertToEuros(order.total)}€</OrderCardSubData>
          </OrderCardContentRow>
          {/* <OrderCardContentRow>
            <OrderCardSubData>Reste à payer : </OrderCardSubData><OrderCardSubData>{convertToEuros(order.total - (order.payments.reduce((acc, payment) => acc + payment.amount, 0) || 0))}€</OrderCardSubData>
          </OrderCardContentRow> */}
        </OrderCardContent>
      )}
    </OrderCardContainer>
  );
});


// LE GESTE DE RELÂCHEMENT, DANS SON PROPRE COMPOSANT. Il porte trois hooks —
// dont `useModal`, qui dépose un `registerModal` dans le store au montage. Dans
// `OrderCard`, ils s'exécutaient pour CHAQUE fiche d'un service (plusieurs
// centaines un soir de rush) alors que le geste ne concerne que les commandes
// d'entraînement. Ici, ils ne montent que là où le geste existe.
const OrderEcoleActions = ({ order }: OrderCardProps) => {
  const toast = useToast();
  const modal = useModal(`ecole-${order.appId}`);
  const [release, { isLoading: releasing }] = fiscalApi.useReleaseFiscalEcoleMutation();

  const retirerEcole = async (reason: string) => {
    try {
      await release({ appId: order.appId, reason }).unwrap();
      toast.success('Le mode école a été retiré : la commande est redevenue ordinaire.');
    } catch (e: any) {
      toast.error(extractApiGenericError(e) ?? 'Le retrait du mode école a échoué.');
    }
    modal.close();
  };

  return (
    <>
      <OrderCardContentDisclaimer>Commande d’entraînement (mode école) : elle n’est comptée dans aucun service.</OrderCardContentDisclaimer>
      {/* PAS de `small` : le variant rend un rond de 34 px qui écrase le libellé
          (même correctif que sur la page Conformité). Sans lui, la pilule prend
          la largeur de son texte ; la ligne la pousse à droite. */}
      <OrderEcoleActionsRow>
        <FilterButton testID="retirer-ecole" title="Retirer le mode école" onPress={() => modal.open()} />
      </OrderEcoleActionsRow>
      <ReleaseEcoleModal id={modal.id} serviceId={order.serviceId} close={modal.close} onConfirm={retirerEcole} isLoading={releasing} />
    </>
  );
};

const OrderEcoleActionsRow = styled.View({
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  paddingBottom: normalize(14),
});

const OrderCardContainer = styled.View<{ isOpen?: boolean }>(({ props }) => ({
  width: '100%',
  backgroundColor: props.isOpen ? theme.colors.babypowder : theme.colors.translucid,
  borderRadius: normalize(42),
}));

const OrderCardTrigger = styled.TouchableOpacity({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  rowGap: normalize(10),
  paddingHorizontal: normalize(21),
  paddingVertical: normalize(21),
  alignItems: 'center',
});

const OrderCardLink = ({ href, children }: { href: string, children: React.ReactNode }) => {
  return (
    <a
      style={{
        color: theme.colors.celadonblue,
        textDecoration: 'underline',
        textDecorationStyle: 'dashed',
        textDecorationColor: theme.colors.celadonblue,
        textUnderlineOffset: 4,
      }}
      href={href}
    >
      {children}
    </a>
  );
}

const OrderCardData = styled.Text<{ first?: boolean, pill?: boolean, status?: OrderStatus, fixed?: boolean, dark?: boolean, pillColor?: string, pillBorderColor?: string }>(({ props }) => ({
  color: (props.first && !props.dark) ? theme.colors.grey : props.dark ? theme.colors.oxfordblue : theme.colors.babypowder,
  fontSize: normalize(16),
  fontWeight: 'bold',
  borderLeftWidth: props.first ? 0 : 1,
  borderLeftColor: "#374857",
  paddingHorizontal: normalize(10),
  width: 'auto',
  textAlign: props.fixed ? 'center' : 'left',
  ...(props.pill && {
    width: 90,
    textAlign: 'center',
    borderRadius: normalize(10),
    paddingHorizontal: normalize(10),
    paddingVertical: normalize(5),
    backgroundColor: props.pillColor || '#374857',
    color: theme.colors.babypowder,
    borderWidth: 1,
    borderColor: props.pillBorderColor || theme.colors.babypowder,
    borderLeftColor: props.pillBorderColor || theme.colors.babypowder,
  }),
  ...(props.status && {
    borderColor: props.status === OrderStatus.CANCELLED ? "#EE0000" : "#374857",
    borderLeftColor: props.status === OrderStatus.CANCELLED ? "#EE0000" : "#374857",
    backgroundColor: props.status === OrderStatus.CANCELLED ? "#EE0000" : "#374857",
  }),
}));

const OrderCardContent = styled.View({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingHorizontal: normalize(21),
  paddingVertical: normalize(21),
  borderTopWidth: 1,
  borderTopColor: '#374857',
  borderStyle: 'dashed',
});

const OrderCardContentRow = styled.View<{ noBorder?: boolean }>(({ props }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingTop: props.noBorder ? 0 : normalize(13),
  paddingBottom: normalize(13),
  borderTopWidth: props.noBorder ? 0 : 1,
  borderTopColor: theme.colors.grey,
  borderStyle: 'dashed',
}));

const OrderCardContentDisclaimer = styled.Text({
  width: '100%',
  color: theme.colors.satanGray,
  fontSize: normalize(14),
  paddingBottom: normalize(14),
  fontStyle: 'italic',
});

const OrderCardSubData = styled.Text({
  color: theme.colors.oxfordblue,
  fontSize: normalize(16),
  fontWeight: 'bold',
});

const getChannelLabel = (channel: OrderChannel) => {
  switch (channel) {
    case OrderChannel.WEBSITE:
      return 'Web';
    case OrderChannel.TICKEAT_OS:
      return 'Caisse';
    case OrderChannel.UBER_EATS:
      return 'Uber Eats';
    case OrderChannel.JUST_EAT:
      return 'Just Eat';
    case OrderChannel.DELIVEROO:
      return 'Deliveroo';
    case OrderChannel.DELICITY:
      return 'Delicity';
    case OrderChannel.KIOSK:
      return 'Borne';
    case OrderChannel.TABLE:
      return 'Table';
    default:
      return String(channel);
  }
};

const getModePillColors = (mode: OrderMode) => {
  switch (mode) {
    case OrderMode.DELIVERY:
      return { bg: theme.colors.celadonblue, border: theme.colors.babypowder };
    case OrderMode.TAKEAWAY:
      return { bg: theme.colors.gold, border: theme.colors.babypowder };
    case OrderMode.ONSPOT:
      return { bg: theme.colors.oxfordblue, border: theme.colors.babypowder };
    default:
      return { bg: '#374857', border: theme.colors.babypowder };
  }
};

const getChannelPillColors = (channel: OrderChannel) => {
  switch (channel) {
    case OrderChannel.WEBSITE:
      return { bg: theme.colors.oxfordblue, border: theme.colors.babypowder };
    case OrderChannel.TICKEAT_OS:
      return { bg: '#374857', border: theme.colors.babypowder };
    case OrderChannel.UBER_EATS:
      return { bg: '#06C167', border: theme.colors.babypowder };
    case OrderChannel.JUST_EAT:
      return { bg: '#FF5A00', border: theme.colors.babypowder };
    case OrderChannel.DELIVEROO:
      return { bg: '#00CCBC', border: theme.colors.babypowder };
    case OrderChannel.DELICITY:
      return { bg: '#0066B3', border: theme.colors.babypowder };
    case OrderChannel.KIOSK:
      return { bg: '#8D269C', border: theme.colors.babypowder };
    case OrderChannel.TABLE:
      return { bg: '#0EA5E9', border: theme.colors.babypowder };
    default:
      return { bg: '#374857', border: theme.colors.babypowder };
  }
};